import { useState, useEffect, useCallback } from "react";
import { Filter, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { brightApi } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 50;

export default function BrightDataBrowser() {
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({ campaigns: [], sources: [] });
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    campaign: "",
    source: "",
  });

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      Object.entries(form).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const res = await brightApi.getDataBrowser(params);
      setRecords(res.data.records);
      setTotal(res.data.total);
      setFilters(res.data.filters);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [form, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = ["ymd", "campaign", "earnings", "peers", "source"];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-4" data-testid="bright-data-browser">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="data-browser-filters">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
          <span className="text-xs text-slate-400 ml-auto">{total.toLocaleString()} records</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Start Date</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => updateForm({ start_date: e.target.value })}
              className="h-9 border-slate-200 text-xs"
              data-testid="browser-start-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">End Date</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => updateForm({ end_date: e.target.value })}
              className="h-9 border-slate-200 text-xs"
              data-testid="browser-end-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Campaign</Label>
            <Select value={form.campaign || "all"} onValueChange={(v) => updateForm({ campaign: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 border-slate-200 text-xs" data-testid="browser-campaign-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {filters.campaigns.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Source</Label>
            <Select value={form.source || "all"} onValueChange={(v) => updateForm({ source: v === "all" ? "" : v })}>
              <SelectTrigger className="h-9 border-slate-200 text-xs" data-testid="browser-source-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {filters.sources.map((s) => (
                  <SelectItem key={s} value={s}>{s === "web_indexing" ? "Web-indexing" : s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="data-browser-table">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-blue-600" size={24} />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {columns.map((col) => (
                  <TableHead key={col} className={`text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 ${["earnings", "peers"].includes(col) ? "text-right" : ""}`}>
                    {col === "ymd" ? "Date" : col.charAt(0).toUpperCase() + col.slice(1)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-12 text-slate-400">
                    No data found. Adjust filters or run a sync.
                  </TableCell>
                </TableRow>
              ) : (
                records.map((row, i) => (
                  <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <TableCell className="py-3 px-4 text-sm text-slate-700">{row.ymd}</TableCell>
                    <TableCell className="py-3 px-4 text-sm text-slate-700">{row.campaign}</TableCell>
                    <TableCell className="py-3 px-4 text-sm text-right font-mono text-slate-700">${row.earnings?.toFixed(2)}</TableCell>
                    <TableCell className="py-3 px-4 text-sm text-right font-mono text-slate-700">{row.peers?.toLocaleString()}</TableCell>
                    <TableCell className="py-3 px-4">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.source === "web_indexing" ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"}`}>
                        {row.source === "web_indexing" ? "Web-indexing" : row.source || "—"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between" data-testid="browser-pagination">
          <span className="text-xs text-slate-500">
            {startIdx.toLocaleString()}–{endIdx.toLocaleString()} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} className="h-8 w-8 p-0" data-testid="browser-prev">
              <ChevronLeft size={16} />
            </Button>
            <span className="text-xs text-slate-600 px-2">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)} className="h-8 w-8 p-0" data-testid="browser-next">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
